import { z } from 'zod';
import { getExecEndpoint } from '../../service/getExecEndpoint';
import { banqoraPortfolioAnalysisApi } from '../servers/defaultServer';

/**
 * Schema for a single allocation item in the batch upload
 */
const AllocationItemSchema = z.object({
  isin: z.string().min(12).max(12), // ISINs are always 12 characters
  allocation_percent: z.number().min(0).max(100),
});

/**
 * Request schema for uploading allocation batch
 */
export const AllocationBatchUploadRequestSchema = z.object({
  allocations: z.array(AllocationItemSchema),
  description: z.string().optional(),
  batch_type: z.string(),
});

/**
 * Response schema for allocation batch upload
 */
export const AllocationBatchUploadResponseSchema = z.object({
  batch_id: z.string().uuid(),
  snapshot_id: z.string().uuid(),
  batch_type: z.string(),
  total_allocations: z.number(),
  created_allocations: z.number(),
  excluded_instruments: z.array(z.string()),
  excluded_count: z.number(),
  status: z.string(),
});

export type AllocationBatchUploadRequest = z.infer<
  typeof AllocationBatchUploadRequestSchema
>;
export type AllocationBatchUploadResponse = z.infer<
  typeof AllocationBatchUploadResponseSchema
>;

export const allocationBatchUploadEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/snapshots',
  description: 'Upload allocation batch for a snapshot',
};

export interface AllocationBatchUploadEndpointDef {
  endpoint: typeof allocationBatchUploadEndpoint;
  methods: {
    POST: {
      response: AllocationBatchUploadResponse;
      payload: AllocationBatchUploadRequest;
    };
  };
}

const endpointAllocationBatchUploadExec =
  getExecEndpoint<AllocationBatchUploadEndpointDef>(
    allocationBatchUploadEndpoint,
  );

/**
 * Upload allocation batch for an existing snapshot
 *
 * This endpoint uploads allocation data for a snapshot using the new batch system.
 * It creates a new allocation batch, processes all ISINs, and links the batch to the snapshot.
 *
 * @param snapshotId - UUID of the existing snapshot
 * @param request - Allocation batch data with ISINs, percentages, and batch type
 * @returns Response with batch creation details and any excluded instruments
 */
export const endpointAllocationBatchUploadPOST = async (
  snapshotId: string,
  request: AllocationBatchUploadRequest,
): Promise<AllocationBatchUploadResponse> => {
  const result = await endpointAllocationBatchUploadExec({
    method: 'POST',
    urlExtra: `/${snapshotId}/allocations/batch`,
    payload: request,
  });
  return result as AllocationBatchUploadResponse;
};

/**
 * Response schema for allocation batch delete
 */
export const AllocationBatchDeleteResponseSchema = z.object({
  snapshot_id: z.string().uuid(),
  batch_id: z.string().uuid(),
  batch_type: z.string(),
  status: z.string(),
});

export type AllocationBatchDeleteResponse = z.infer<
  typeof AllocationBatchDeleteResponseSchema
>;

export interface AllocationBatchDeleteEndpointDef {
  endpoint: typeof allocationBatchUploadEndpoint;
  methods: {
    DELETE: {
      response: AllocationBatchDeleteResponse;
    };
  };
}

const endpointAllocationBatchDeleteExec =
  getExecEndpoint<AllocationBatchDeleteEndpointDef>(
    allocationBatchUploadEndpoint,
  );

/**
 * Delete a batch (current_holdings, universe, benchmark, or proposed) from a snapshot.
 * Only removes the batch link; the snapshot remains.
 */
export const endpointAllocationBatchDeleteDELETE = async (
  snapshotId: string,
  batchId: string,
): Promise<AllocationBatchDeleteResponse> => {
  const result = await endpointAllocationBatchDeleteExec({
    method: 'DELETE',
    urlExtra: `/${snapshotId}/allocations/batch/${batchId}`,
  });
  if (!result) {
    throw new Error('Delete batch request failed');
  }
  return result;
};
